/**
 * useQrLabels — the engine behind the QR Label Generator (PBI #18337).
 *
 * The module is split in two:
 *  1. Pure, dependency-free functions (short-code generation, payload
 *     building, the fabric readability meter, Template01 mapping, roster/CSV
 *     normalisation). These carry the business rules and are unit-tested.
 *  2. A `useQrLabels` composable that wraps rendering (bwip-js) and export
 *     (ExcelJS / PNG / SVG / print). bwip-js and ExcelJS are lazy-imported so
 *     they never bloat the main bundle and never load in the unit-test graph.
 */
import type { OrderForBarcode } from '@/models';
import {
  DEFAULT_QR_SETTINGS,
  ONE_D_SYMBOLOGIES,
  type Ecc,
  type EncodeMode,
  type QrLabelItem,
  type QrSettings,
  type ReadabilityVerdict,
  type Symbology,
} from '@/models/QrLabelGenerator';

/* ------------------------------------------------------------------ *
 * Pure helpers — business rules, no DOM / no third-party deps.
 * ------------------------------------------------------------------ */

/** True when the symbology is a linear (1D) barcode carrying the code only. */
export function is1D(symbology: Symbology): boolean {
  return ONE_D_SYMBOLOGIES.includes(symbology);
}

/**
 * Auto-generate a short code from Order No + Size, guaranteed unique within
 * the supplied `taken` set. The set is mutated so sequential calls stay unique
 * across a whole batch. Resolver lookups need globally-unique codes — that is
 * a Phase-2 backend concern; here we only guarantee batch uniqueness.
 */
export function generateShortCode(
  orderNo: string | undefined,
  size: string | undefined,
  taken: Set<string> = new Set(),
): string {
  const base =
    [orderNo, size]
      .filter((p) => p !== null && p !== undefined && String(p).trim() !== '')
      .join('-')
      .toUpperCase()
      .replace(/\s+/g, '') || 'CODE';

  if (!taken.has(base)) {
    taken.add(base);
    return base;
  }
  // Collision — append an incrementing suffix until unique.
  let n = 2;
  let candidate = `${base}-${n}`;
  while (taken.has(candidate)) {
    n += 1;
    candidate = `${base}-${n}`;
  }
  taken.add(candidate);
  return candidate;
}

/**
 * Build the string that gets encoded into the symbol for one item.
 * 1D symbologies always carry the bare code (encode mode is ignored).
 */
export function buildPayload(item: QrLabelItem, settings: QrSettings): string {
  if (is1D(settings.symbology)) return item.code;

  switch (settings.encodeMode) {
    case 'fields': {
      // Encode only the fields the user ticked (Order / SKU / Size), in that
      // order. Falls back to the bare code so the symbol is never empty when
      // nothing is selected or every selected field is blank.
      const parts = [
        settings.includeOrder ? item.order_no : undefined,
        settings.includeSku ? (item.sku_name ?? item.product_name) : undefined,
        settings.includeSize ? item.size : undefined,
      ].filter((p) => p !== null && p !== undefined && String(p).trim() !== '');
      return parts.length ? parts.join(' / ') : item.code;
    }
    case 'resolver':
      return `${settings.shortDomain.replace(/\/+$/, '')}/?${item.code}`;
    case 'short':
    default:
      return item.code;
  }
}

/** Byte-mode capacity per QR version (index = version - 1), versions 1..10. */
const QR_BYTE_CAPACITY: Record<Ecc, number[]> = {
  L: [17, 32, 53, 78, 106, 134, 154, 192, 230, 271],
  M: [14, 26, 42, 62, 84, 106, 122, 152, 180, 213],
  Q: [11, 20, 32, 46, 60, 74, 86, 108, 130, 151],
  H: [7, 14, 24, 34, 44, 58, 64, 84, 98, 119],
};

/** Modules-per-side for a QR symbol sized to hold `byteLen` bytes at `ecc`. */
function qrModuleCount(byteLen: number, ecc: Ecc): number {
  const table = QR_BYTE_CAPACITY[ecc];
  for (let v = 0; v < table.length; v += 1) {
    if (byteLen <= table[v]) return 17 + 4 * (v + 1);
  }
  // Beyond the tabulated range — estimate, clamped to the v40 maximum (177).
  const perVersion = table[table.length - 1] - table[table.length - 2];
  const extra = Math.ceil((byteLen - table[table.length - 1]) / Math.max(perVersion, 1));
  return Math.min(17 + 4 * (10 + extra), 177);
}

/** Approximate modules-per-side for the other 2D symbologies (guidance only). */
function moduleCountFor(symbology: Symbology, payload: string, ecc: Ecc): number {
  const len = payload.length;
  switch (symbology) {
    case 'qr':
      return qrModuleCount(len, ecc);
    case 'microqr':
      // M2..M4 (11 = M1 is numeric-only, excluded).
      if (len <= 5) return 13;
      if (len <= 10) return 15;
      return 17;
    case 'datamatrix':
      if (len <= 6) return 14;
      if (len <= 16) return 18;
      if (len <= 30) return 22;
      return 26;
    default:
      return 0; // 1D — not applicable.
  }
}

/**
 * The fabric readability meter. Computes mm-per-module for 2D symbols and
 * returns a verdict. Rev-15 rule: a Standard QR under ~10 mm on fabric is
 * always ❌ regardless of the mm/module maths (stock phone cameras can't read
 * it). 1D symbols return an informational verdict.
 */
export function computeReadability(settings: QrSettings, payload = ''): ReadabilityVerdict {
  if (is1D(settings.symbology)) {
    return {
      level: 'info',
      text: 'ℹ︎ 1D barcode',
      mmPerModule: 0,
      moduleCount: 0,
      minMmForHalf: 0,
      detail:
        'Linear symbol — readability depends on bar width & scanner, not mm/module. Best for warehouse scanners.',
    };
  }

  const n = moduleCountFor(settings.symbology, payload, settings.ecc);
  const size = Number.isFinite(settings.printSizeMm) ? settings.printSizeMm : 0;
  const mmPerModule = n > 0 ? size / n : 0;
  const minMmForHalf = 0.5 * n;

  let level: ReadabilityVerdict['level'];
  let text: string;
  if (settings.symbology === 'qr' && size < 10) {
    level = 'bad';
    text = '❌ Too small for QR on fabric';
  } else if (mmPerModule >= 0.5) {
    level = 'ok';
    text = '✅ Good for fabric';
  } else if (mmPerModule >= 0.4) {
    level = 'warn';
    text = '⚠ Borderline — test print';
  } else {
    level = 'bad';
    text = '❌ Too small to scan';
  }

  const detail =
    `${mmPerModule.toFixed(2)} mm/module · ${n}×${n} modules · ` +
    `min ${minMmForHalf.toFixed(1)} mm for 0.5 mm/module` +
    (settings.symbology === 'qr' ? ' · Standard QR needs ≈ ≥10 mm on fabric' : '');

  return { level, text, mmPerModule, moduleCount: n, minMmForHalf, detail };
}

/**
 * Normalise one roster entry (from `order/{no}/for/barcode`) into a label.
 * The roster's `barcode_string` is treated as the code; if absent, a short
 * code is generated from order no + size.
 */
export function normalizeRosterItem(
  entry: OrderForBarcode['barcode_data'][number],
  orderNo: string,
  taken: Set<string>,
): QrLabelItem {
  const code =
    entry.barcode_string?.trim() || generateShortCode(orderNo, entry.size, taken);
  if (entry.barcode_string?.trim()) taken.add(code);
  return {
    product_name: entry.product_name,
    sku_name: entry.product_name,
    design_name: entry.design_name,
    name_font_name: entry.name_font_name,
    number_font_name: entry.number_font_name,
    logo1: entry.logo1,
    player_name: entry.player_name,
    player_number: entry.player_number,
    size: entry.size,
    quantity: entry.quantity ?? 1,
    page_reference: entry.page_reference,
    order_no: orderNo,
    code,
  };
}

/**
 * Expand items into one entry per unit of `quantity` (minimum 1 each) — the
 * same expansion the barcode modal applies, so the count of rendered/exported
 * labels matches the physical label count. A missing/invalid quantity counts
 * as a single label.
 */
export function expandByQuantity(items: QrLabelItem[]): QrLabelItem[] {
  return items.flatMap((item) => {
    const qty = Math.max(1, Math.floor(item.quantity ?? 1));
    return Array.from({ length: qty }, () => item);
  });
}

/** Template01 columns, in the exact order Waleed specified. */
export const TEMPLATE01_COLUMNS = [
  { header: 'Product Name', key: 'product_name', width: 24 },
  { header: 'Design Name', key: 'design_name', width: 20 },
  { header: 'Name', key: 'player_name', width: 16 },
  { header: 'Name Font Name', key: 'name_font_name', width: 16 },
  { header: 'Number', key: 'player_number', width: 10 },
  { header: 'Number Font Name', key: 'number_font_name', width: 16 },
  { header: 'Size', key: 'size', width: 8 },
  { header: 'Quantity', key: 'quantity', width: 10 },
  { header: 'Logo1', key: 'logo1', width: 14 },
  { header: 'QR Code', key: 'qr', width: 14 },
] as const;

/** Build the ordered Template01 cell values for one item (QR column blank). */
export function toTemplate01Row(item: QrLabelItem): Array<string | number> {
  return [
    item.product_name ?? '',
    item.design_name ?? '',
    item.player_name ?? '',
    item.name_font_name ?? '',
    item.player_number ?? '',
    item.number_font_name ?? '',
    item.size ?? '',
    item.quantity ?? '',
    item.logo1 ?? '',
    '', // QR Code — embedded image is placed over this cell.
  ];
}

/* ------------------------------------------------------------------ *
 * Rendering + export — DOM / bwip-js / ExcelJS (lazy-loaded).
 * ------------------------------------------------------------------ */

/** bwip-js barcode id (bcid) per symbology. */
const SYMBOLOGY_BCID: Record<Symbology, string> = {
  qr: 'qrcode',
  microqr: 'microqrcode',
  datamatrix: 'datamatrix',
  code128: 'code128',
  code39: 'code39',
};

function symbologyToBcid(symbology: Symbology): string {
  return SYMBOLOGY_BCID[symbology];
}

/** bwip-js expects hex colours without the leading '#'. */
function hex(color: string): string {
  return color.replace(/^#/, '');
}

/** bwip-js options for the primary symbol of an item. */
function bwipOptions(item: QrLabelItem, settings: QrSettings): Record<string, unknown> {
  const symbology = settings.symbology;
  return {
    bcid: symbologyToBcid(symbology),
    text: buildPayload(item, settings),
    scale: 3,
    ...(symbology === 'qr' || symbology === 'microqr' ? { eclevel: settings.ecc } : {}),
    ...(is1D(symbology) ? { height: 10, includetext: true, textxalign: 'center' } : {}),
    barcolor: hex(settings.dotColor),
    backgroundcolor: hex(settings.bgColor),
    paddingwidth: 0,
    paddingheight: 0,
  };
}

export function useQrLabels() {
  /** Render one item's symbol onto a canvas element. */
  async function renderToCanvas(canvas: HTMLCanvasElement, item: QrLabelItem, settings: QrSettings) {
    const bwipjs = (await import('bwip-js/browser')).default;
    bwipjs.toCanvas(canvas, bwipOptions(item, settings) as never);
  }

  /** Render one item's symbol as an SVG string. */
  async function renderToSvg(item: QrLabelItem, settings: QrSettings): Promise<string> {
    const bwipjs = (await import('bwip-js/browser')).default;
    // toSVG is synchronous in bwip-js v4's browser build.
    return (bwipjs as unknown as { toSVG: (o: unknown) => string }).toSVG(
      bwipOptions(item, settings),
    );
  }

  /** Render one item to a PNG data URL (used for the Excel embed + PNG export). */
  async function renderToPngDataUrl(item: QrLabelItem, settings: QrSettings): Promise<string> {
    const canvas = document.createElement('canvas');
    await renderToCanvas(canvas, item, settings);
    return canvas.toDataURL('image/png');
  }

  /** Trigger a browser download for a blob. */
  function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /** Download every item's symbol as individual SVG files. */
  async function downloadAllSvg(items: QrLabelItem[], settings: QrSettings) {
    for (let i = 0; i < items.length; i += 1) {
      const svg = await renderToSvg(items[i], settings);
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      downloadBlob(blob, `label-${items[i].code || 'code'}-${i + 1}.svg`);
    }
  }

  /** Download each item's symbol as an individual PNG file. */
  async function downloadPng(items: QrLabelItem[], settings: QrSettings) {
    for (let i = 0; i < items.length; i += 1) {
      const dataUrl = await renderToPngDataUrl(items[i], settings);
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      downloadBlob(blob, `label-${items[i].code || 'code'}-${i + 1}.png`);
    }
  }

  /**
   * Build and download the Template01 Excel workbook with the generated
   * symbol embedded per row, preset column widths and a frozen header.
   */
  async function exportTemplate01Xlsx(
    items: QrLabelItem[],
    settings: QrSettings,
    fileName = 'qr-labels-template01.xlsx',
  ) {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Labels');

    ws.columns = TEMPLATE01_COLUMNS.map((c) => ({ header: c.header, key: c.key, width: c.width }));
    ws.views = [{ state: 'frozen', ySplit: 1 }];
    ws.getRow(1).font = { bold: true };

    const qrColIndex = TEMPLATE01_COLUMNS.findIndex((c) => c.key === 'qr'); // zero-based

    for (let i = 0; i < items.length; i += 1) {
      const excelRow = ws.addRow(toTemplate01Row(items[i]));
      excelRow.height = 60;
      const dataUrl = await renderToPngDataUrl(items[i], settings);
      const imageId = wb.addImage({ base64: dataUrl, extension: 'png' });
      // Place image over the QR Code cell (row index is zero-based here).
      ws.addImage(imageId, {
        tl: { col: qrColIndex, row: excelRow.number - 1 },
        ext: { width: 72, height: 72 },
      });
    }

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    downloadBlob(blob, fileName);
  }

  return {
    DEFAULT_QR_SETTINGS,
    renderToCanvas,
    renderToSvg,
    renderToPngDataUrl,
    downloadBlob,
    downloadAllSvg,
    downloadPng,
    exportTemplate01Xlsx,
    // Re-export pure helpers for convenience in the component.
    buildPayload,
    computeReadability,
    generateShortCode,
    normalizeRosterItem,
    toTemplate01Row,
  };
}

export type { EncodeMode, QrLabelItem, QrSettings, Symbology };
